import Image from '@segment/matcha/components/Image'

import TabsLayoutBase, * as TabsLayout from '@segment/matcha/recipes/TabsLayout'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import useTabs from '@segment/matcha/recipes/Tabs/useTabs'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import buildHighPerformance1Webp from './assets/BuildHighPerformance-1.webp'
import buildHighPerformance1Png2x from './assets/BuildHighPerformance-1@2x.png'
import buildHighPerformance1Webp2x from './assets/BuildHighPerformance-1@2x.webp'

import buildHighPerformance2Webp from './assets/BuildHighPerformance-2.webp'
import buildHighPerformance2Png2x from './assets/BuildHighPerformance-2@2x.png'
import buildHighPerformance2Webp2x from './assets/BuildHighPerformance-2@2x.webp'

import buildHighPerformance3Webp from './assets/BuildHighPerformance-3.webp'
import buildHighPerformance3Png2x from './assets/BuildHighPerformance-3@2x.png'
import buildHighPerformance3Webp2x from './assets/BuildHighPerformance-3@2x.webp'

const copy = {
  heading: `Build high-performance teams`,
  description: `By making analytics easy to access, you’ll enable your team to become truly data-driven.`,
}

const tabs = [
  {
    key: `DEMOCRATIZE_DATA_ACCESS_SLIDE_KEY`,
    title: `Democratize data access`,
    description: `Teams move faster when everyone has access to SQL. Enable data warehousing with the flip of a switch.`,
    image: {
      png2x: buildHighPerformance1Png2x,
      webp: buildHighPerformance1Webp,
      webp2x: buildHighPerformance1Webp2x,
      width: 620,
      height: 420,
    },
  },
  {
    key: `ATTRIBUTE_FEATURE_EXPERIMENTS_SLIDE_KEY`,
    title: `Attribute feature experiments`,
    description: `Study how new features affect your product’s KPI, by enabling a product analytics tool.`,
    image: {
      png2x: buildHighPerformance2Png2x,
      webp: buildHighPerformance2Webp,
      webp2x: buildHighPerformance2Webp2x,
      width: 589,
      height: 389,
    },
  },
  {
    key: `MEASURE_IMPROVE_KPI_SLIDE_KEY`,
    title: `Measure & improve KPIs`,
    description: `By making analytics easy to access, your team will see making decisions using data as the norm.`,
    image: {
      png2x: buildHighPerformance3Png2x,
      webp: buildHighPerformance3Webp,
      webp2x: buildHighPerformance3Webp2x,
      width: 589,
      height: 389,
    },
  },
]

const warehousesLink = {
  text: `Explore our Warehouses Product`,
  url: `/product/warehouses/`,
}

const QueryDataSection = () => {
  const pageConfig = usePageConfig()
  const { getTrackProps, getControlProps, getPanelProps, activeTabKey } = useTabs(0, tabs.length, `query-data-section`)

  return (
    <SectionBase variant="centered">
      <Section.Copy>
        <CopyTextBase centered={true}>
          <CopyText.Heading variant="h2" tag="h2">
            {copy.heading}
          </CopyText.Heading>
          <CopyText.Description>{copy.description}</CopyText.Description>
        </CopyTextBase>
      </Section.Copy>

      <Section.Contents>
        <TabsLayoutBase reverse={true}>
          <TabsLayout.Controls {...getTrackProps()}>
            {tabs.map((tab, index) => (
              <TabsLayout.Control {...getControlProps(index)} key={tab.key}>
                {tab.title}
              </TabsLayout.Control>
            ))}
          </TabsLayout.Controls>

          {tabs.map((tab, index) => (
            <TabsLayout.Content {...getPanelProps(index)} active={index === activeTabKey} key={tab.key}>
              <TabsLayout.Copy>
                <CopyTextBase>
                  <CopyText.Heading variant="h2">{tab.title}</CopyText.Heading>
                  <CopyText.Description variant="pLarge">{tab.description}</CopyText.Description>
                  <CopyText.Link href={getAbsoluteUrl(warehousesLink.url, pageConfig)}>
                    {warehousesLink.text}
                  </CopyText.Link>
                </CopyTextBase>
              </TabsLayout.Copy>

              <TabsLayout.Media>
                <picture>
                  <source type="image/webp" srcSet={`${tab.image.webp}, ${tab.image.webp2x} 2x`} />
                  <Image
                    alt={tab.title}
                    width={tab.image.width}
                    height={tab.image.height}
                    src={tab.image.png2x}
                    loading="lazy"
                  />
                </picture>
              </TabsLayout.Media>
            </TabsLayout.Content>
          ))}
        </TabsLayoutBase>
      </Section.Contents>
    </SectionBase>
  )
}

export default QueryDataSection
